import {Spinner} from "@heroui/spinner";
import {Dropdown, DropdownTrigger, DropdownMenu, DropdownItem} from "@heroui/react";
import {useQuery} from "@tanstack/react-query";
import {createFileRoute, Link, useNavigate} from '@tanstack/react-router'
import {BadgeCheck, Clock, DollarSign, ExternalLink, MapPin, Search, Trophy} from 'lucide-react'
import {useState} from 'react'

import {config} from "../config/config";
import LandingNav from "../landing/components/LandingNav.tsx";
import ArrowUpRightIcon from "../landing/icons/ArrowUpRightIcon.tsx";
import PodiumIcon from "../landing/icons/PodiumIcon.tsx";
import RefreshIcon from "../landing/icons/RefreshIcon.tsx";
import StarIcon from "../landing/icons/StarIcon.tsx";
import FooterSection from "../landing/sections/FooterSection.tsx";
import services from "../services";

export const Route = createFileRoute('/find-tournaments')({
  component: RouteComponent,
})

const SPORTS = [
  {value: '', label: 'All sports'},
  {value: 'padel', label: 'Padel'},
  {value: 'tennis', label: 'Tennis'},
  {value: 'pickelball', label: 'Pickleball'},
  {value: 'squash', label: 'Squash'},
  {value: 'badminton', label: 'Badminton'},
]

const PHASE_CHIP: Record<string, {label: string; cls: string}> = {
  register:         {label: 'Registration Open', cls: 'bg-[#e7f8ef] text-[#14a653]'},
  register_live:    {label: 'Started · Registration Open', cls: 'bg-[#e7f8ef] text-[#14a653]'},
  full:             {label: 'Full', cls: 'bg-[#fdf0e3] text-[#d9822b]'},
  generating_draws: {label: 'Generating Draws', cls: 'bg-[#e8effd] text-[#2563eb]'},
  happening:        {label: 'Live', cls: 'bg-[#e7f8ef] text-[#14a653]'},
  finished:         {label: 'Completed', cls: 'bg-[#eef2fb] text-[#5770ab]'},
  not_open:         {label: 'Opening Soon', cls: 'bg-[#eef2fb] text-[#5770ab]'},
  closed:           {label: 'Closed', cls: 'bg-[#eef2fb] text-[#5770ab]'},
}

function ordinal(n: number) {
  const s = ['th', 'st', 'nd', 'rd']
  const v = n % 100
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`
}

function fmtDateTime(iso?: string | null) {
  if (!iso) return 'Date TBC'
  const d = new Date(iso)
  const weekday = d.toLocaleDateString('en-US', {weekday: 'short'})
  const month = d.toLocaleDateString('en-US', {month: 'short'})
  const time = d.toLocaleTimeString('en-US', {hour: 'numeric', minute: '2-digit'}).replace(':00', '').replace(' ', '')
  return `${weekday} ${ordinal(d.getDate())}, ${month} • ${time}`
}

function RouteComponent() {
  const navigate = useNavigate()
  const [sport, setSport] = useState('')
  const [q, setQ] = useState('')
  const [sort, setSort] = useState('')

  const listQuery = useQuery({
    queryKey: ['public-events', sport, q, sort],
    queryFn:  () => services.publicEvent.list({sport: sport || undefined, q: q || undefined, sort: sort || undefined}),
  })

  const events: any[] = listQuery.data?.events || []

  return (
    <main className="min-h-screen bg-[#EDEEF4] font-inter text-[#071a5f]">
      <title>Find Tournaments & Leagues Near You | ScorePal</title>
      <meta name="description" content="Browse and register for upcoming padel, tennis, pickleball, squash and badminton tournaments and leagues on ScorePal. Find events near you and sign up in minutes." />
      <link rel="canonical" href={`${config.publicUrl}/find-tournaments/`} />
      {/* Ad rails now come from FooterSection so every public sub-page shares one unit. */}
      {/* Hero — compact, matches /tournament-formats/ */}
      <section className="relative overflow-hidden p-4">
        <div className="rounded-4xl bg-linear-to-br from-[#0a1530] via-[#0d1b3d] to-[#082F8A] pb-12">
          <div className="relative z-20">
            <LandingNav />
          </div>

          <div className="container mx-auto px-6 pt-8 lg:px-12">
            <div className="max-w-3xl">
              <span className="mb-5 inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-sm font-semibold text-white/90">
                <StarIcon className="text-white" size={16} />
                Tournaments &amp; leagues across every sport
              </span>

              <h1 className="mb-5 font-bebas text-[clamp(40px,6vw,72px)] font-bold uppercase leading-none tracking-wide text-white">
                Find your next competition
              </h1>

              <p className="mb-8 max-w-2xl font-inter text-base leading-relaxed text-white/85 lg:text-xl">
                Discover and register for upcoming tournaments and leagues near you — padel, tennis,
                pickleball, squash, badminton and more. Find your next event and elevate your game.
              </p>

              <div className="flex flex-wrap items-center gap-3">
                <Dropdown placement="bottom-start">
                  <DropdownTrigger>
                    <button
                      type="button"
                      className="inline-flex items-center gap-3 rounded-full bg-[#EDEEF4] py-1 pl-6 pr-1 font-semibold text-[#082F8A] shadow-lg transition hover:bg-white"
                    >
                      <span className="text-base font-medium tracking-wide">Add Your Tournament</span>
                      <div className="rounded-full bg-[#082F8A]">
                        <ArrowUpRightIcon className="text-white" size={40} />
                      </div>
                    </button>
                  </DropdownTrigger>
                  <DropdownMenu aria-label="Add your tournament">
                    <DropdownItem
                      key="scorepal"
                      description="Set up and run your tournament on ScorePal"
                      onPress={() => navigate({to: "/signup/organizer/" as any})}
                    >
                      Run it on ScorePal
                    </DropdownItem>
                    <DropdownItem
                      key="placement"
                      description="List a tournament you run elsewhere in our directory"
                      onPress={() => navigate({to: "/list-tournament/" as any})}
                    >
                      List an external tournament
                    </DropdownItem>
                  </DropdownMenu>
                </Dropdown>

                <div className="flex flex-wrap gap-3">
                  <HeroBadge icon={<PodiumIcon className="text-white" size={18} />} label="All Tournaments" />
                  <HeroBadge icon={<RefreshIcon className="text-white" size={18} />} label="Updated Daily" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 pb-20 lg:px-12">
        <div className="relative z-20 mt-14 flex flex-col gap-3 rounded-3xl border border-[#dfe6f5] bg-white p-4 shadow-[0_18px_45px_rgba(8,47,138,0.12)] sm:flex-row sm:items-center">
          <div className="flex flex-1 items-center gap-2 rounded-2xl border border-[#e3e8f5] bg-[#f5f7fc] px-3">
            <Search size={18} className="text-[#5770ab]" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by tournament name..."
              className="w-full bg-transparent py-2.5 text-sm font-medium outline-none"
            />
          </div>
          <select value={sport} onChange={(e) => setSport(e.target.value)}
                  className="rounded-2xl border border-[#e3e8f5] bg-[#f5f7fc] px-3 py-2.5 text-sm font-medium outline-none sm:w-44">
            {SPORTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <select value={sort} onChange={(e) => setSort(e.target.value)}
                  className="rounded-2xl border border-[#e3e8f5] bg-[#f5f7fc] px-3 py-2.5 text-sm font-medium outline-none sm:w-44">
            <option value="">Newest first</option>
            <option value="popular">Most popular</option>
          </select>
        </div>

        <div className="mb-5 mt-7 flex items-end justify-between">
          <h3 className="font-bebas text-2xl font-bold uppercase tracking-wide text-[#06195f] sm:text-3xl">Tournaments</h3>
          {!listQuery.isLoading ? (
            <span className="text-sm font-semibold text-[#5770ab]">Showing {events.length} {events.length === 1 ? 'tournament' : 'tournaments'}</span>
          ) : null}
        </div>

        {listQuery.isLoading ? (
          <div className="flex justify-center py-16"><Spinner size="lg" /></div>
        ) : events.length === 0 ? (
          <div className="py-16 text-center text-[#5770ab]">
            <Trophy className="mx-auto mb-3 text-[#9aa8c9]" size={44} />
            <p className="font-bold">No tournaments found</p>
            <p className="text-sm">Try a different sport or search.</p>
          </div>
        ) : (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {events.map((e) => e.type === 'placement'
              ? <PlacementCard key={e.uid} p={e} />
              : <EventCard key={`${e.type}-${e.uid}`} e={e} />)}
          </div>
        )}
      </div>

      <FooterSection />
    </main>
  )
}

function PlacementCard({p}: {p: any}) {
  const poster = p.public_card_image_url || p.banner_url
  const subtitle = p.sport_type ? String(p.sport_type) : null
  return (
    <a href={p.external_url || '#'} target="_blank" rel="noopener noreferrer"
       className="group flex flex-col overflow-hidden rounded-[26px] border border-[#dfe6f5] bg-white shadow-[0_10px_30px_rgba(8,47,138,0.08)] transition hover:-translate-y-1 hover:shadow-[0_18px_45px_rgba(8,47,138,0.16)]">
      <div className="relative aspect-[4/5] overflow-hidden bg-gradient-to-br from-[#082f8a] to-[#18a85d]">
        {poster
          ? <img src={poster} alt={p.title} className="h-full w-full object-cover transition duration-500 group-hover:scale-105" />
          : <div className="grid h-full place-items-center text-white/80"><Trophy size={48} /></div>}
        <span className="absolute right-3 top-3 rounded-full bg-[#eef2fb] px-2.5 py-1 text-[11px] font-bold text-[#5770ab]">Listed</span>
        <div className="absolute inset-x-0 bottom-0 flex justify-center bg-gradient-to-t from-black/45 to-transparent pb-4 pt-10">
          <span className="rounded-full bg-white px-5 py-2 text-sm font-bold text-[#082f8a] shadow-lg transition group-hover:bg-[#082f8a] group-hover:text-white">Visit Site</span>
        </div>
      </div>
      <div className="flex flex-1 flex-col gap-3 p-4">
        <div className="min-w-0">
          <h3 className="truncate text-base font-black text-[#06195f]">{p.title}</h3>
          {subtitle ? <p className="truncate text-xs font-medium capitalize text-[#8094bf]">{subtitle}</p> : null}
        </div>
        <div className="grid gap-2">
          {p.venue ? <InfoRow icon={<MapPin size={14} />}>{p.venue}</InfoRow> : null}
          {p.starts_at ? <InfoRow icon={<Clock size={14} />}>{fmtDateTime(p.starts_at)}</InfoRow> : null}
        </div>
        <div className="mt-auto pt-1">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-[#cdd9f2] px-5 py-2 text-sm font-bold text-[#082f8a] transition group-hover:border-[#082f8a] group-hover:bg-[#082f8a] group-hover:text-white">
            Visit <ExternalLink size={14} />
          </span>
        </div>
      </div>
    </a>
  )
}

function HeroBadge({icon, label}: {icon: React.ReactNode; label: string}) {
  return (
    <div className="flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 backdrop-blur-md">
      {icon}
      <span className="text-sm font-medium text-white">{label}</span>
    </div>
  )
}

function EventCard({e}: {e: any}) {
  const chip = PHASE_CHIP[e.registration_phase] ?? PHASE_CHIP.closed
  const fee = e.entry_amount ? `${e.entry_currency_code || ''} ${(e.entry_amount / 100).toLocaleString()}`.trim() : 'Free'
  const location = [e.venue, e.city].filter(Boolean).join(', ')
  const subtitle = [e.sport_type ? String(e.sport_type) : null, e.grade_default ? `Level ${e.grade_default}` : null].filter(Boolean).join(' · ')
  const organizer = e.organizer

  return (
    <Link to="/event/$uid/" params={{uid: e.slug || e.uid}}
          className="group flex flex-col overflow-hidden rounded-[26px] border border-[#dfe6f5] bg-white shadow-[0_10px_30px_rgba(8,47,138,0.08)] transition hover:-translate-y-1 hover:shadow-[0_18px_45px_rgba(8,47,138,0.16)]">
      {/* Poster */}
      <div className="relative aspect-[4/5] overflow-hidden bg-gradient-to-br from-[#082f8a] to-[#18a85d]">
        {(e.public_card_image_url || e.banner_url) ? (
          <img src={e.public_card_image_url || e.banner_url} alt={e.title} className="h-full w-full object-cover transition duration-500 group-hover:scale-105" />
        ) : (
          <div className="grid h-full place-items-center text-white/80"><Trophy size={48} /></div>
        )}
        <span className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-white/95 px-2.5 py-1 text-[11px] font-bold text-[#082f8a] shadow">
          <BadgeCheck size={13} /> ScorePal Verified
        </span>
        <span className={`absolute right-3 top-3 rounded-full px-2.5 py-1 text-[11px] font-bold ${chip.cls}`}>{chip.label}</span>
        <div className="absolute inset-x-0 bottom-0 flex justify-center bg-gradient-to-t from-black/45 to-transparent pb-4 pt-10">
          <span className="rounded-full bg-white px-5 py-2 text-sm font-bold text-[#082f8a] shadow-lg transition group-hover:bg-[#082f8a] group-hover:text-white">
            View Details
          </span>
        </div>
      </div>

      {/* Body */}
      <div className="flex flex-1 flex-col gap-3 p-4">
        <div className="flex items-center gap-3">
          <Avatar src={organizer?.image} fallback={e.title} />
          <div className="min-w-0">
            <h3 className="truncate text-base font-black text-[#06195f]">{e.title}</h3>
            {subtitle ? <p className="truncate text-xs font-medium capitalize text-[#8094bf]">{subtitle}</p> : null}
          </div>
        </div>

        <div className="grid gap-2">
          {location ? <InfoRow icon={<MapPin size={14} />}>{location}</InfoRow> : null}
          <InfoRow icon={<Clock size={14} />}>{fmtDateTime(e.starts_at)}</InfoRow>
          <InfoRow icon={<DollarSign size={14} />}>{fee}</InfoRow>
        </div>

        {organizer ? (
          <div className="flex items-center gap-2 border-t border-[#eef1f8] pt-3 text-xs font-medium text-[#8094bf]">
            <Avatar src={organizer.image} fallback={organizer.name} size={20} />
            <span className="truncate">Organized by <span className="font-semibold text-[#2168ff]">{organizer.name}</span></span>
          </div>
        ) : null}

        <div className="mt-auto pt-1">
          <span className="inline-flex items-center rounded-full border border-[#cdd9f2] px-5 py-2 text-sm font-bold text-[#082f8a] transition group-hover:border-[#082f8a] group-hover:bg-[#082f8a] group-hover:text-white">
            Open
          </span>
        </div>
      </div>
    </Link>
  )
}

function InfoRow({icon, children}: {icon: React.ReactNode; children: React.ReactNode}) {
  return (
    <div className="flex items-center gap-2.5">
      <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-[#082f8a] text-white">{icon}</span>
      <span className="truncate text-sm font-semibold text-[#27325c]">{children}</span>
    </div>
  )
}

function Avatar({src, fallback, size = 40}: {src?: string | null; fallback?: string; size?: number}) {
  if (src) {
    return <img src={src} alt="" style={{height: size, width: size}} className="shrink-0 rounded-full border border-[#e3e8f5] object-cover" />
  }
  return (
    <div style={{height: size, width: size}}
         className="grid shrink-0 place-items-center rounded-full bg-[#eef4ff] text-sm font-black text-[#2168ff]">
      {(fallback || '?').charAt(0).toUpperCase()}
    </div>
  )
}
